import type { ReactNode } from 'react';

/**
 * Rendu du corps d'un message.
 *
 * C'est ici que se tient — ou s'effondre — la promesse faite au sprint 1 :
 * « le corps d'un message est du JSON structuré, jamais du HTML ».
 *
 * Le serveur peut valider tout ce qu'il veut, si le client finit par faire
 * un `innerHTML` la propriété est perdue. Ce fichier ne contient donc
 * AUCUN `dangerouslySetInnerHTML`, et il n'en contiendra jamais : React
 * construit des nœuds texte, le contenu n'est jamais interprété comme du
 * balisage.
 *
 * Un type de nœud inconnu n'est pas rendu « au mieux » : il est ignoré.
 * Un client plus ancien que le serveur affichera un message incomplet
 * plutôt qu'un message dont il ne comprend pas une partie.
 */

export type Inline =
  | { type: 'text'; text: string; marks?: Array<'bold' | 'italic' | 'strike' | 'code'> }
  | { type: 'mention'; user_id: string; label: string }
  | { type: 'channel'; channel_id: string; label: string }
  | { type: 'link'; href: string; label: string }
  | { type: 'broadcast'; target: 'channel' | 'here' | 'everyone' };

export type Block =
  | { type: 'paragraph'; content: Inline[] }
  | { type: 'quote'; content: Inline[] }
  | { type: 'code_block'; lang?: string; code: string }
  | { type: 'bullet_list' | 'ordered_list'; items: Inline[][] };

export type Doc = { type: 'doc'; content: Block[] };

const LIBELLE_DIFFUSION = {
  channel: '@canal',
  here: '@présents',
  everyone: '@tous',
} as const;

function rendreInline(n: Inline, cle: number, moi?: string): ReactNode {
  switch (n.type) {
    case 'text': {
      // `n.text` arrive dans un nœud texte React : quel que soit son
      // contenu — balises, guillemets, script — il s'affiche tel quel.
      let noeud: ReactNode = n.text;
      for (const marque of n.marks ?? []) {
        if (marque === 'bold') noeud = <strong key={marque}>{noeud}</strong>;
        else if (marque === 'italic') noeud = <em key={marque}>{noeud}</em>;
        else if (marque === 'strike') noeud = <s key={marque}>{noeud}</s>;
        else if (marque === 'code') noeud = <code key={marque} className="ligne-code">{noeud}</code>;
      }
      return <span key={cle}>{noeud}</span>;
    }
    case 'mention':
      return (
        <span key={cle} className={'mention' + (n.user_id === moi ? ' moi' : '')}>
          @{n.label}
        </span>
      );
    case 'channel':
      return <span key={cle} className="lien-canal">#{n.label}</span>;
    case 'broadcast':
      return <span key={cle} className="mention diffusion">{LIBELLE_DIFFUSION[n.target]}</span>;
    case 'link':
      // `rel` complet : un lien collé par un collègue ne doit pas donner à
      // la page d'arrivée une poignée sur l'onglet qui l'a ouverte.
      return (
        <a key={cle} href={sur(n.href)} target="_blank" rel="noopener noreferrer nofollow">
          {n.label}
        </a>
      );
    default:
      return null;
  }
}

/**
 * Une URL qui n'est ni http ni https n'est pas rendue cliquable.
 * Le serveur refuse déjà `javascript:` à l'écriture ; on ne fait pas
 * dépendre la sécurité du client d'une validation faite ailleurs.
 */
function sur(href: string): string {
  try {
    const u = new URL(href);
    return u.protocol === 'http:' || u.protocol === 'https:' ? u.toString() : '#';
  } catch {
    return '#';
  }
}

export function RendreCorps({ doc, moi }: { doc: Doc; moi?: string }) {
  if (!doc || doc.type !== 'doc' || !Array.isArray(doc.content)) return null;
  return (
    <>
      {doc.content.map((bloc, i) => {
        switch (bloc.type) {
          case 'paragraph':
            return <p key={i}>{bloc.content.map((n, j) => rendreInline(n, j, moi))}</p>;
          case 'quote':
            return (
              <blockquote key={i}>
                {bloc.content.map((n, j) => rendreInline(n, j, moi))}
              </blockquote>
            );
          case 'code_block':
            // Le code est du texte, pas du balisage : pas de coloration
            // syntaxique qui repasserait par du HTML construit à la main.
            return (
              <pre key={i} className="bloc-code">
                <code>{bloc.code}</code>
              </pre>
            );
          case 'bullet_list':
          case 'ordered_list': {
            const Liste = bloc.type === 'ordered_list' ? 'ol' : 'ul';
            return (
              <Liste key={i}>
                {bloc.items.map((item, j) => (
                  <li key={j}>{item.map((n, k) => rendreInline(n, k, moi))}</li>
                ))}
              </Liste>
            );
          }
          default:
            return null;
        }
      })}
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// Composition
// ─────────────────────────────────────────────────────────────

/**
 * Transforme la saisie en document structuré.
 *
 * Les mentions sont reconnues à partir d'un annuaire fourni : on ne
 * fabrique jamais une mention à partir du seul texte. Écrire « @canal-projet »
 * ne déclenche donc pas une diffusion générale — le nœud n'est produit que
 * pour les libellés exacts.
 */
export function composer(
  texte: string,
  annuaire: Array<{ id: string; display_name: string }> = [],
): Doc {
  const lignes = texte.replace(/\r\n/g, '\n').split('\n');
  const blocs: Block[] = [];
  let dansCode = false;
  let tampon: string[] = [];
  let langue: string | undefined;

  const viderCode = () => {
    blocs.push(langue ? { type: 'code_block', lang: langue, code: tampon.join('\n') }
                      : { type: 'code_block', code: tampon.join('\n') });
    tampon = [];
    langue = undefined;
  };

  for (const ligne of lignes) {
    if (ligne.startsWith('```')) {
      if (dansCode) { viderCode(); dansCode = false; }
      else { dansCode = true; langue = ligne.slice(3).trim() || undefined; }
      continue;
    }
    if (dansCode) { tampon.push(ligne); continue; }
    if (ligne.trim() === '') continue;
    if (ligne.startsWith('> ')) {
      blocs.push({ type: 'quote', content: decouper(ligne.slice(2), annuaire) });
      continue;
    }
    blocs.push({ type: 'paragraph', content: decouper(ligne, annuaire) });
  }
  if (dansCode && tampon.length > 0) viderCode();

  if (blocs.length === 0) blocs.push({ type: 'paragraph', content: [{ type: 'text', text: '' }] });
  return { type: 'doc', content: blocs };
}

const DIFFUSIONS: Record<string, 'channel' | 'here' | 'everyone'> = {
  '@canal': 'channel', '@channel': 'channel',
  '@présents': 'here', '@presents': 'here', '@here': 'here',
  '@tous': 'everyone', '@everyone': 'everyone',
};

function decouper(
  ligne: string,
  annuaire: Array<{ id: string; display_name: string }>,
): Inline[] {
  const sortie: Inline[] = [];
  let reste = ligne;

  while (reste.length > 0) {
    const at = reste.indexOf('@');
    if (at === -1) { pousserTexte(sortie, reste); break; }

    // Une diffusion : mot exact, jamais un préfixe. « @canal-projet »
    // reste du texte.
    const motDiffusion = Object.keys(DIFFUSIONS).find(
      (m) => reste.startsWith(m, at) && !/[\w-]/.test(reste[at + m.length] ?? ''),
    );
    if (motDiffusion) {
      pousserTexte(sortie, reste.slice(0, at));
      sortie.push({ type: 'broadcast', target: DIFFUSIONS[motDiffusion]! });
      reste = reste.slice(at + motDiffusion.length);
      continue;
    }

    // Une personne : le libellé doit correspondre exactement à un nom de
    // l'annuaire. Le plus long d'abord, pour que « Jean Dupont » l'emporte
    // sur « Jean ».
    const personne = [...annuaire]
      .sort((a, b) => b.display_name.length - a.display_name.length)
      .find((u) => reste.startsWith('@' + u.display_name, at));
    if (personne) {
      pousserTexte(sortie, reste.slice(0, at));
      sortie.push({ type: 'mention', user_id: personne.id, label: personne.display_name });
      reste = reste.slice(at + 1 + personne.display_name.length);
      continue;
    }

    pousserTexte(sortie, reste.slice(0, at + 1));
    reste = reste.slice(at + 1);
  }

  return sortie.length > 0 ? sortie : [{ type: 'text', text: '' }];
}

function pousserTexte(sortie: Inline[], texte: string): void {
  if (texte.length === 0) return;
  const dernier = sortie[sortie.length - 1];
  if (dernier?.type === 'text') dernier.text += texte;
  else sortie.push({ type: 'text', text: texte });
}

/** Aperçu d'une ligne, pour la liste des canaux et les notifications. */
export function apercu(doc: Doc): string {
  if (!doc?.content) return '';
  const bouts: string[] = [];
  for (const b of doc.content) {
    if (b.type === 'code_block') bouts.push(b.code);
    else if (b.type === 'bullet_list' || b.type === 'ordered_list')
      for (const it of b.items) bouts.push(it.map(texteDe).join(''));
    else if (b.type === 'paragraph' || b.type === 'quote')
      bouts.push(b.content.map(texteDe).join(''));
  }
  return bouts.join(' ').replace(/\s+/g, ' ').trim();
}

function texteDe(n: Inline): string {
  if (n.type === 'text') return n.text;
  if (n.type === 'mention') return '@' + n.label;
  if (n.type === 'channel') return '#' + n.label;
  if (n.type === 'broadcast') return LIBELLE_DIFFUSION[n.target];
  return n.label;
}
